import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  subsystemsApi,
  type SubsystemDto,
  type CreateSubsystemBody,
  type UpdateSubsystemBody,
  type CreateSubsystemSectionBody,
  type UpdateSubsystemSectionBody,
  type CreateSubsystemItemBody,
  type UpdateSubsystemItemBody,
} from '@api/subsystemsApi';

const ACQ_TREE_KEY = ['subsystems', 'tree'] as const;

export function useSubsystemTree(acqIncludeInactive = false) {
  return useQuery<SubsystemDto[]>({
    queryKey: [...ACQ_TREE_KEY, acqIncludeInactive],
    queryFn: () => subsystemsApi.getTree(acqIncludeInactive),
    staleTime: 60_000,
  });
}

function acqUseInvalidateTree() {
  const acqQc = useQueryClient();
  return () => acqQc.invalidateQueries({ queryKey: ACQ_TREE_KEY });
}

export function useCreateSubsystem() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqBody: CreateSubsystemBody) => subsystemsApi.createSubsystem(acqBody),
    onSuccess: acqInv,
  });
}

export function useUpdateSubsystem() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqBody: UpdateSubsystemBody) => subsystemsApi.updateSubsystem(acqBody),
    onSuccess: acqInv,
  });
}

export function useDeleteSubsystem() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqId: string) => subsystemsApi.deleteSubsystem(acqId),
    onSuccess: acqInv,
  });
}

export function useReorderSubsystems() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: ({ acqParentId, acqOrderedIds }: { acqParentId: string | null; acqOrderedIds: string[] }) =>
      subsystemsApi.reorderSubsystems(acqParentId, acqOrderedIds),
    onSuccess: acqInv,
  });
}

export function useCreateSection() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqBody: CreateSubsystemSectionBody) => subsystemsApi.createSection(acqBody),
    onSuccess: acqInv,
  });
}

export function useUpdateSection() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqBody: UpdateSubsystemSectionBody) => subsystemsApi.updateSection(acqBody),
    onSuccess: acqInv,
  });
}

export function useDeleteSection() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqId: string) => subsystemsApi.deleteSection(acqId),
    onSuccess: acqInv,
  });
}

export function useCreateItem() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqBody: CreateSubsystemItemBody) => subsystemsApi.createItem(acqBody),
    onSuccess: acqInv,
  });
}

export function useUpdateItem() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqBody: UpdateSubsystemItemBody) => subsystemsApi.updateItem(acqBody),
    onSuccess: acqInv,
  });
}

export function useDeleteItem() {
  const acqInv = acqUseInvalidateTree();
  return useMutation({
    mutationFn: (acqId: string) => subsystemsApi.deleteItem(acqId),
    onSuccess: acqInv,
  });
}
